import { useEffect, useState } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { DataTable } from '@/components/data-table'
import { Button } from '@/components/ui/button'
import FilterDrawer, { type FilterValues } from '@/components/FilterDrawer'
import { SettingsIcon, FilterIcon, ExportIcon } from '@/components/Icons'
import type { SortingState } from '@tanstack/react-table'
import { UserActionDialog } from '@/components/UserActionDialog'
import { showSuccessToast } from '../ui/toast'
import { handleAxiosError, safeDownloadFile } from '@/utils/helper'
import AdPreviewDrawer from './AdPreviewDrawer'
import SponsorDemographicsData from './SponsorDemographicsData'
import {
  ReportGenerationDrawer,
  type ReportFormData,
} from './ReportGenerationDrawer'
import {
  exportDemographicData,
  getAdvertisementList,
  updateAdvertisementFrequency,
  type ExportDemographicParams,
} from '@/api/sponsorApi'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { useQuery, useMutation } from '@tanstack/react-query'
import type { SponsorShipAdvertisementType } from '@/schemas/sponsor.schems'
import type { Advertisement } from '../sponsorship/AdvertisementColumns'
import {
  ADID_PARAM,
  SPONSOR_TAB,
  transformAdvertisementData,
} from '@/constants/sponsor'
import { EventSelect, type EventSelectValue } from '../event/EventSelect'
import { getadvertisementColumns } from './advertisement-columns'
import FilterBar from '../FilterBar'
import type { UserSelectValue } from '../UserSelect'
import { useSearchParams } from 'react-router-dom'
import {
  SubEventSelect,
  type SubEventSelectValue,
} from '../sub-event/SubEventSelect'

interface SponsorAdDataProps {
  search: string
  handleShowSearch: (val: boolean) => void
}

function SponsorAdData({
  search,
  handleShowSearch,
}: Readonly<SponsorAdDataProps>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [activeTab, setActiveTab] = useState('advertisements')
  const [isAdSettingsOpen, setIsAdSettingsOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Filter states
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<EventSelectValue | null>(
    null
  )
  const [selectedSubEvent, setSelectedSubEvent] =
    useState<SubEventSelectValue | null>(null)
  // State to track selected sponsors for FilterBar display
  const [selectedSponsors, setSelectedSponsors] = useState<UserSelectValue[]>(
    []
  )

  const [appliedFilters, setAppliedFilters] = useState<FilterValues>({
    userRole: [],
    subscriptionType: '',
    eventAttendance: '',
    registrationStartDate: '',
    registrationEndDate: '',
    status: [],
    statusEvent: '',
    category: [],
    purchaseDate: '',
    eventId: '',
    bySponsor: [],
    adStatus: [],
    placement: '',
  })

  // Report generation states
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  // State for pagination
  const [pagination, setPagination] = useState({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  })
  const [selectedAd, setSelectedAd] = useState<string | null>(null)
  const [isAdDrawerOpen, setIsAdDrawerOpen] = useState<boolean>(false)

  const [searchParams] = useSearchParams()

  useEffect(() => {
    const queryAdId = searchParams.get(ADID_PARAM)

    if (queryAdId) {
      setSelectedAd(queryAdId)
      setIsAdDrawerOpen(true)
    }
  }, [])

  useEffect(() => {
    if (activeTab == SPONSOR_TAB.ADVERTISEMENT) {
      handleShowSearch(true)
    } else {
      handleShowSearch(false)
    }
  }, [activeTab])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const handleAdSettings = () => {
    console.log('Ad settings clicked')
    setIsAdSettingsOpen(true)
  }

  const handleFilterApply = (filters: FilterValues) => {
    // Remove adStatus if empty or not set
    const cleanedFilters = {
      ...filters,
      adStatus: Array.isArray(filters.adStatus)
        ? filters.adStatus.filter(Boolean)
        : [],
    }
    setAppliedFilters(cleanedFilters)
    setFilterDrawerOpen(false)
    showSuccessToast('Filters applied')
  }

  const handleSponsorsChange = (sponsors: UserSelectValue[]) => {
    setSelectedSponsors(sponsors)
  }

  // Convert selectedSponsors to the format FilterBar expects
  const sponsorOptions = selectedSponsors.map(sponsor => ({
    id: sponsor.id,
    name: `${sponsor.firstName} ${sponsor.lastName}`.trim(),
  }))

  const handleReportGeneration = async (reportData: ReportFormData) => {
    try {
      setIsGeneratingReport(true)
      console.log('Generating report with data:', reportData)

      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 2000))

      showSuccessToast('Report generated successfully!')
      setReportDrawerOpen(false)
    } catch (error) {
      console.error('Error generating report:', error)
      showSuccessToast('Failed to generate report. Please try again.')
    } finally {
      setIsGeneratingReport(false)
    }
  }

  const { data: advertisementList, isLoading: isLoadingAdvertisementList } =
    useQuery({
      queryKey: [
        SPONSOR_QUERY_KEYS.advertisementList,
        sorting,
        currentPage,
        pageSize,
        selectedEvent,
        appliedFilters,
        search,
        selectedSubEvent,
      ],
      queryFn: () =>
        getAdvertisementList({
          sort: sorting,
          limit: pageSize,
          page: currentPage,
          eventId: selectedEvent?.id,
          sponsorIds: appliedFilters.bySponsor,
          startDate: appliedFilters.registrationStartDate,
          endDate: appliedFilters.registrationEndDate,
          search,
          status: appliedFilters.adStatus,
          subEventId: selectedSubEvent?.id,
          isAdminRequest: true,
        }),
    })

  const { mutate: updateFrequencyMutation, isPending: isUpdatingFrequency } =
    useMutation({
      mutationFn: updateAdvertisementFrequency,
      onSuccess: () => {
        showSuccessToast('Ad frequency settings updated successfully')
        setIsAdSettingsOpen(false)
      },
      onError: err => {
        handleAxiosError('Failed to update ad frequency settings.', err)
      },
    })

  const handleUpdateFrequency = (formData: { duration: number }) => {
    const rotationFrequency = formData.duration
    updateFrequencyMutation(rotationFrequency)
  }

  const rawAdvertisements: SponsorShipAdvertisementType[] =
    advertisementList?.data?.result || []
  const advertisements: Advertisement[] =
    rawAdvertisements?.map(transformAdvertisementData) || []
  const totalItems = advertisementList?.data?.totalCount || 0
  const totalPages =
    advertisementList?.data?.totalPages || Math.ceil(totalItems / pageSize)

  const handleEventSelectChange = (
    event: EventSelectValue | EventSelectValue[] | null
  ) => {
    let selectedEventData: EventSelectValue | null = null
    if (event) {
      if (Array.isArray(event)) {
        selectedEventData = event[0] || null
      } else {
        selectedEventData = event
      }
    }

    setSelectedEvent(selectedEventData)
  }

  const handleADClick = (ad: Advertisement) => {
    setSelectedAd(ad.id)
    setIsAdDrawerOpen(true)
  }

  const handleRemoveFilter = (key: keyof FilterValues, id?: string) => {
    if (Array.isArray(appliedFilters[key]) && id) {
      const newFilters = {
        ...appliedFilters,
        [key]: appliedFilters[key].filter(itemId => itemId !== id),
      }
      setAppliedFilters(newFilters)

      // If removing a bySponsor filter, also update selectedSponsors
      if (key === 'bySponsor') {
        setSelectedSponsors(prev => prev.filter(sponsor => sponsor.id !== id))
      }
    } else {
      const newFilters = {
        ...appliedFilters,
        [key]: key === 'userRole' ? [] : '',
      }
      if (key === 'eventId') {
        setSelectedEvent(null)
      }
      // If clearing bySponsor filters completely, also clear selectedSponsors
      if (key === 'bySponsor') {
        setSelectedSponsors([])
      }
      setAppliedFilters(newFilters)
    }
  }

  const handleResetFilters = () => {
    const emptyFilters: FilterValues = {
      userRole: [],
      subscriptionType: '',
      eventAttendance: '',
      registrationEndDate: '',
      registrationStartDate: '',
      // registrationDate: '', --- IGNORE ---
      status: [],
      statusEvent: '',
      category: [],
      purchaseDate: '',
      eventId: '',
      bySponsor: [],
      adStatus: [],
      placement: '',
    }
    setAppliedFilters(emptyFilters)
    setSelectedEvent(null)
    setSelectedSubEvent(null)
    // Also clear selected sponsors when resetting all filters
    setSelectedSponsors([])
  }

  const exportMutation = useMutation({
    mutationFn: (params: ExportDemographicParams) =>
      exportDemographicData(params),
    onSuccess: blob => {
      safeDownloadFile({ data: blob, fileName: 'demographics-export.csv' })
    },
    onError: error => {
      handleAxiosError(
        'Export Failed',
        error,
        'No demographic data available for export.'
      )
    },
  })

  const handleExportClick = () => {
    exportMutation.mutate({
      ...(selectedEvent && { eventId: selectedEvent.id }),
    })
  }

  const handleSubEventSelectChange = (
    event: SubEventSelectValue | SubEventSelectValue[] | null
  ) => {
    let selectedEventData: SubEventSelectValue | null = null
    if (event) {
      if (Array.isArray(event)) {
        selectedEventData = event[0] || null
      } else {
        selectedEventData = event
      }
    }

    setSelectedSubEvent(selectedEventData)
  }

  return (
    <>
      {/* Main Content Section */}
      <section aria-label="Advertisements & Demographics" className="mt-6">
        <div className="p-4 lg:p-6">
          <div className="flex gap-4 items-center justify-between mb-1 lg:mb-2 w-full flex-wrap">
            <h2 className="text-xl font-bold text-ss-neutral-950 leading-7">
              Advertisement Campaigns
            </h2>
            {/* Controls Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Event Select */}
                <div className="flex gap-2">
                  <EventSelect
                    variant="compact"
                    value={selectedEvent}
                    onValueChange={handleEventSelectChange}
                    placeholder="Select Main Event"
                    enableSearch
                  />
                  {activeTab === SPONSOR_TAB.ADVERTISEMENT && (
                    <SubEventSelect
                      variant="compact"
                      value={selectedSubEvent}
                      onValueChange={handleSubEventSelectChange}
                      placeholder="Select Sub Event"
                      enableSearch
                    />
                  )}
                </div>

                {/* Filter Button */}
                {activeTab === SPONSOR_TAB.ADVERTISEMENT && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilterDrawerOpen(true)}
                    // className="h-9 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-25 bg-white"
                  >
                    <FilterIcon className="h-4 w-4" />
                    Filter
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
              aria-label="Ad management sections"
            >
              <div className="flex items-center justify-between w-full flex-wrap gap-1">
                <TabsList className="flex items-center h-auto gap-1.5 p-1 bg-transparent w-fit flex-wrap">
                  <TabsTrigger
                    value="advertisements"
                    className="bg-transparent text-ss-neutral-950 px-4 py-1.5 lg:px-6 lg:py-3 rounded-[36px] text-sm font-semibold transition-colors hover:bg-ss-neutral-25 data-[state=active]:bg-ss-blue-900 data-[state=active]:text-white h-auto"
                  >
                    Advertisements
                  </TabsTrigger>
                  <TabsTrigger
                    value={SPONSOR_TAB.DEMOGRAPHICS}
                    className="bg-transparent text-ss-neutral-950 px-4 py-1.5 lg:px-6 lg:py-3 rounded-[36px] text-sm font-semibold hover:bg-ss-neutral-25 data-[state=active]:bg-ss-blue-900 data-[state=active]:text-white h-auto transition-colors"
                  >
                    Audience Demographics
                  </TabsTrigger>
                </TabsList>

                {activeTab === SPONSOR_TAB.DEMOGRAPHICS ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleExportClick}
                    className="h-9 bg-ss-blue-900 text-white hover:bg-ss-blue-800"
                    aria-label="Generate demographics report"
                  >
                    <ExportIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                    Export CSV
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAdSettings}
                    className="h-9 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-25"
                    aria-label="Open advertisement settings"
                  >
                    <SettingsIcon className="h-4 w-4" aria-hidden="true" />
                    Ad Settings
                  </Button>
                )}
              </div>

              <TabsContent
                value={SPONSOR_TAB.ADVERTISEMENT}
                id="advertisements-content"
                aria-labelledby="advertisements-tab"
              >
                <div
                  className="sticky top-[73px] z-10 bg-transparent"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <FilterBar
                    filters={appliedFilters}
                    onRemoveFilter={handleRemoveFilter}
                    onResetFilters={handleResetFilters}
                    selectedEvent={selectedEvent}
                    sponsorOptions={sponsorOptions}
                  />
                </div>
                <DataTable<Advertisement, unknown>
                  tableFilterSkeleton={false}
                  parentClassName="lg:mx-0"
                  data={advertisements}
                  columns={getadvertisementColumns({
                    onADNameClick: handleADClick,
                  })}
                  sorting={sorting}
                  setSorting={setSorting}
                  pagination={pagination}
                  setPagination={setPagination}
                  showFilter={false}
                  showExport={false}
                  isLoading={isLoadingAdvertisementList}
                  error={null}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  emptyState={{
                    image: '/auth/empty-ad.svg',
                    title: 'No Active Ads',
                    subtitle:
                      'There are currently no sponsor ads running on the platform. Ads will appear here once sponsors upload and activate their campaigns.',
                  }}
                />
              </TabsContent>

              <TabsContent
                value="demographics"
                className="mt-6"
                id="demographics-content"
                aria-labelledby="demographics-tab"
              >
                <SponsorDemographicsData selectedEvent={selectedEvent} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterDrawerOpen}
        onOpenChange={setFilterDrawerOpen}
        onApplyFilters={handleFilterApply}
        onSponsorsChange={handleSponsorsChange}
        fields={[
          'bySponsor',
          'registrationStartDate',
          'registrationEndDate',
          'adStatus',
        ]}
        fieldOptions={{
          placement: [
            'Hero sponsor',
            'In-feed native card',
            'Right-rail display',
            'Mobile sticky footer',
            'Event detail',
          ],
        }}
        appliedFilter={appliedFilters}
        buttonText="Save & Apply Parameters"
      />

      {/* Reject confirmation dialog */}
      <UserActionDialog
        open={isAdSettingsOpen}
        onOpenChange={setIsAdSettingsOpen}
        showForm={false}
        danger={false}
        title="General Ad Settings"
        subtext="Manage default configurations for sponsor ads, including display rules, scheduling, and placement preferences to ensure consistent ad performance and branding."
        confirmButtonText="Save & Apply Parameters"
        onConfirm={handleUpdateFrequency}
        isLoading={isUpdatingFrequency}
        rotationFrequency={true}
        generalIcon={true}
      />

      {/* Report Generation Drawer */}
      <ReportGenerationDrawer
        open={reportDrawerOpen}
        onOpenChange={setReportDrawerOpen}
        onGenerateReport={handleReportGeneration}
        isLoading={isGeneratingReport}
      />
      <AdPreviewDrawer
        adId={selectedAd ? selectedAd : ''}
        open={isAdDrawerOpen}
        onOpenChange={setIsAdDrawerOpen}
      />
    </>
  )
}

export default SponsorAdData
